// Solid chip colors, one per pilot. Each pilot keeps the same color index for good
// (users.calendar_color), so adding a pilot never changes anyone else's color. The
// palette is deliberately stronger than the pastel plane colors used for event
// blocks so a chip reads as a tag on top of them.
const chipClasses = [
  'bg-emerald-600 text-white',
  'bg-orange-500 text-white',
  'bg-fuchsia-600 text-white',
  'bg-cyan-600 text-white',
  'bg-rose-600 text-white',
  'bg-violet-600 text-white',
  'bg-lime-600 text-white',
  'bg-amber-500 text-white',
  'bg-teal-600 text-white',
  'bg-blue-600 text-white',
]

export const missingChipClasses = 'bg-red-600 text-white'

export function pilotChipClasses(colorIndex) {
  if (colorIndex === undefined || colorIndex === null || colorIndex < 0)
    return 'bg-gray-500 text-white'

  return chipClasses[colorIndex % chipClasses.length]
}

export function initials(name) {
  return (name || '')
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part[0].toUpperCase())
    .slice(0, 3)
    .join('')
}

export function roleLabel(role) {
  return { pic: 'PIC', sic: 'SIC' }[role] || 'Pilot'
}

function roleRank(pilot) {
  return pilot.pivot?.role === 'pic' ? 0 : 1
}

// A leg's crew with the PIC first
export function sortCrew(pilots) {
  return (pilots || [])
    .slice()
    .sort((a, b) => roleRank(a) - roleRank(b) || a.name.localeCompare(b.name))
}

// One chip per crew member, like "PIC MO". With warnings on, a missing PIC or
// SIC gets a red chip so gaps in coverage stand out.
export function crewChips(pilots, showWarnings = false) {
  const crew = sortCrew(pilots)

  const chips = crew.map((pilot) => ({
    text: roleLabel(pilot.pivot?.role) + ' ' + initials(pilot.name),
    classes: pilotChipClasses(pilot.calendar_color),
    title: roleLabel(pilot.pivot?.role) + ': ' + pilot.name,
    // So a chip can be dragged to another flight
    pilot,
    role: pilot.pivot?.role,
  }))

  if (!showWarnings) return chips

  const missing = (text) => ({
    text,
    classes: missingChipClasses,
    title: text + ' assigned',
  })

  if (!crew.length) {
    chips.push(missing('No pilots'))
  } else {
    if (!crew.some((pilot) => pilot.pivot?.role === 'pic'))
      chips.unshift(missing('No PIC'))

    if (!crew.some((pilot) => pilot.pivot?.role === 'sic'))
      chips.push(missing('No SIC'))
  }

  return chips
}

export function crewSummary(pilots) {
  const crew = sortCrew(pilots)

  if (!crew.length) return 'No pilots assigned'

  return crew
    .map((pilot) => roleLabel(pilot.pivot?.role) + ': ' + pilot.name)
    .join(', ')
}

// "Tue, Sep 30" from the leg's formatted departure date
export function legShortDate(leg) {
  return (leg.formatted_departure_date || '').split(',').slice(0, 2).join(',')
}

export function legRoute(leg) {
  return `${leg.departure_airport.display_code} to ${leg.arrival_airport.display_code}`
}

export function roleOn(leg, pilot) {
  return (leg.pilots || []).find((el) => el.id === pilot.id)?.pivot?.role
}

// "PIC on every leg", or "SIC: CUB to TEB (Tue, Sep 30), TEB to CUB (Wed, Oct 1)"
export function pilotSummary(pilot, legs) {
  const flown = (legs || [])
    .map((leg) => ({ leg, role: roleOn(leg, pilot) }))
    .filter((el) => el.role)

  if (!flown.length) return 'Not on any leg'

  const roles = [...new Set(flown.map((el) => el.role))]

  if (flown.length === legs.length && roles.length === 1)
    return `${roleLabel(roles[0])} on every leg`

  return roles
    .map(
      (role) =>
        `${roleLabel(role)}: ` +
        flown
          .filter((el) => el.role === role)
          .map((el) => `${legRoute(el.leg)} (${legShortDate(el.leg)})`)
          .join(', ')
    )
    .join(' · ')
}
